/**
 * Who is called up, and in what order.
 *
 * A call-up is not the team sheet. It says who travels; it does not have to say
 * who starts, and most of the time it should not — a player who reads that he is
 * on the bench on Thursday has three days to arrive already beaten. The coach
 * decides, so revealing the eleven is a choice made per match rather than a
 * property of the document.
 *
 * Pure, so the shape of a squad can be tested without a canvas or a database.
 */

export interface CallUpPlayer {
  playerId: string;
  firstName: string;
  lastName: string;
  shirtNumber?: number | null;
  /** starter | bench | unavailable */
  role: string;
  positionKey?: string | null;
}

export interface CallUpGroup {
  /** `squad` when the eleven is not revealed. */
  key: 'squad' | 'starting' | 'bench';
  players: CallUpPlayer[];
}

/**
 * Surname order.
 *
 * Shirt-number order is how a team sheet is written, and it is the wrong order
 * here: half a squad has no number before the season settles, and the numbers on
 * the sheet count the call-up rather than name a shirt. Alphabetical is how a
 * player finds himself on a list of twenty in a moving bus.
 */
function byName(a: CallUpPlayer, b: CallUpPlayer): number {
  return (
    a.lastName.localeCompare(b.lastName, undefined, { sensitivity: 'base' }) ||
    a.firstName.localeCompare(b.firstName, undefined, { sensitivity: 'base' })
  );
}

export function calledUp(
  squad: readonly CallUpPlayer[],
  options: { revealEleven?: boolean } = {},
): CallUpGroup[] {
  const selected = squad.filter((p) => p.role === 'starter' || p.role === 'bench');
  if (selected.length === 0) return [];

  if (!options.revealEleven) {
    return [{ key: 'squad', players: [...selected].sort(byName) }];
  }

  const groups: CallUpGroup[] = [];
  for (const key of ['starting', 'bench'] as const) {
    const role = key === 'starting' ? 'starter' : 'bench';
    const players = selected.filter((p) => p.role === role).sort(byName);
    if (players.length > 0) groups.push({ key, players });
  }
  return groups;
}

/** How many players the call-up names, whatever way it is grouped. */
export function calledUpCount(squad: readonly CallUpPlayer[]): number {
  return squad.filter((p) => p.role === 'starter' || p.role === 'bench').length;
}
